import { useCallback, useEffect, useState } from "react";
import { HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const VouchButton = ({ userId, ownerName }) => {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const isMe = me && me.user_id === userId;

  const load = useCallback(() => {
    api.get(`/users/${userId}/vouches`).then(({ data }) => setData(data)).catch(() => {});
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const toggle = async () => {
    if (!me) { toast.info("Sign in to vouch for traders"); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (data.vouched_by_me) {
        const { data: d } = await api.delete(`/users/${userId}/vouch`);
        setData((prev) => ({ ...prev, vouched_by_me: false, count: d.count }));
      } else {
        const { data: d } = await api.post(`/users/${userId}/vouch`);
        setData((prev) => ({ ...prev, vouched_by_me: true, count: d.count }));
        toast.success(`You vouched for ${ownerName || "this trader"} 🤝`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update vouch");
    } finally {
      setBusy(false);
    }
  };

  if (isMe) {
    if (!data.count) return null;
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0654BA]" data-testid="vouch-count-self">
        <HeartHandshake className="w-4 h-4" /> {data.count} {data.count === 1 ? "vouch" : "vouches"}
      </span>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid="vouch-button"
      className={`btn-pill !text-sm !py-2 transition-colors disabled:opacity-60 ${
        data.vouched_by_me
          ? "bg-[#E0EDFA] text-[#0654BA] border border-[#0654BA]/30"
          : "btn-soft"
      }`}
      title={data.vouched_by_me ? "Remove your vouch" : `Publicly vouch for ${ownerName || "this trader"}`}
    >
      <HeartHandshake className="w-4 h-4" />
      {data.vouched_by_me ? "Vouched" : "Vouch"}
      {data.count > 0 && <span className="ml-0.5 tabular-nums">· {data.count}</span>}
    </button>
  );
};

export default VouchButton;
